import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { DELIVERY_WIDGET_BODY_HTML } from '../components/deliveryWidgetMarkup';
import SideMenu from '../components/SideMenu';
import { DEMO_WIDGET_CONFIG } from './demoWidgetConfig';
import './demo.css';

const WIDGET_VERSION = '0.1.9';
const WIDGET_CDN_BASE = `https://cdn.jsdelivr.net/npm/@filuet/widget@${WIDGET_VERSION}`;
const WIDGET_SCRIPT_URL = `${WIDGET_CDN_BASE}/dist/widget.iife.js`;
const WIDGET_STYLE_URL = `${WIDGET_CDN_BASE}/dist/widget.css`;
const FLAG_ICONS_STYLE_URL =
  'https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/css/flag-icons.min.css';
const LIBPHONENUMBER_SCRIPT_URL =
  'https://cdn.jsdelivr.net/npm/libphonenumber-js@1.11.18/bundle/libphonenumber-max.js';

function parseFloatOrNull(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function parseNonNegativeFloatOrNull(value) {
  const numeric = parseFloatOrNull(value);
  if (numeric == null || numeric < 0) return null;
  return numeric;
}

function parsePositiveIntOrNull(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric) || !Number.isInteger(numeric) || numeric <= 0) {
    return null;
  }
  return numeric;
}

function createEmptyItem() {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, sku: '', quantity: '1' };
}

function generateOrderNumber() {
  const ticks = String(Date.now());
  return `LRK${ticks.slice(-7)}`;
}

function normalizeItems(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const sku = String(item?.sku || '').trim();
      const quantity = parsePositiveIntOrNull(item?.quantity);
      if (!sku || quantity == null) return null;
      return { sku, quantity };
    })
    .filter(Boolean);
}

function resolveUrl(base, path) {
  const root = String(base || '').replace(/\/+$/, '');
  const suffix = String(path || '').startsWith('/') ? path : `/${path}`;
  if (!root) {
    return suffix;
  }
  return `${root}${suffix}`;
}

/**
 * Make service base URLs absolute against the console origin.
 * Relative paths like /api/auth-identity must NOT be passed into the widget
 * iframe: it sets <base href="cdn.jsdelivr.net/...">, so relative fetches
 * wrongly go to the CDN (mixed/CORS nonsense).
 */
function absolutizeServiceUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return window.location.origin.replace(/\/+$/, '');
  }
  try {
    return new URL(raw, window.location.origin).toString().replace(/\/+$/, '');
  } catch {
    return window.location.origin.replace(/\/+$/, '');
  }
}

function getDemoServiceUrls() {
  return {
    apiBaseUrl: absolutizeServiceUrl(DEMO_WIDGET_CONFIG.apiBaseUrl),
    deliveryCoreUrl: absolutizeServiceUrl(DEMO_WIDGET_CONFIG.deliveryCoreUrl),
    authIdentityUrl: absolutizeServiceUrl(DEMO_WIDGET_CONFIG.authIdentityUrl),
    clientCoreUrl: absolutizeServiceUrl(DEMO_WIDGET_CONFIG.clientCoreUrl),
  };
}

function buildWidgetSrcdoc(config) {
  const configJson = JSON.stringify(config).replace(/</g, '\\u003c');
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <base href="${WIDGET_CDN_BASE}/" />
  <link rel="stylesheet" href="${WIDGET_STYLE_URL}" />
  <link rel="stylesheet" href="${FLAG_ICONS_STYLE_URL}" />
  <script>window.FILUETSHIP_WIDGET_CONFIG = ${configJson};<\/script>
</head>
<body>
${DELIVERY_WIDGET_BODY_HTML}
<script src="${LIBPHONENUMBER_SCRIPT_URL}"><\/script>
<script src="${WIDGET_SCRIPT_URL}"><\/script>
</body>
</html>`;
}

async function exchangeApiKeyToken(authIdentityUrl, apiKey) {
  const url = resolveUrl(authIdentityUrl, '/v1/auth/api-key/token');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'X-API-Key': apiKey },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail || `API key exchange failed (${res.status})`);
  }
  const data = await res.json();
  const token = data?.access_token;
  if (!token) {
    throw new Error('API key exchange returned no access_token');
  }
  return data;
}

async function createCalculationPayload({ deliveryCoreUrl, token, companyId, body }) {
  const url = resolveUrl(deliveryCoreUrl, '/v1/calculation-payloads');
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Environment': body.environment || 'TEST',
  };
  if (companyId) {
    headers['X-Company-Id'] = String(companyId);
  }
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const err = await res.json();
      detail = err?.detail || JSON.stringify(err);
    } catch {
      detail = await res.text().catch(() => '');
    }
    throw new Error(detail || `Failed to create calculation payload (${res.status})`);
  }
  return res.json();
}

export default function Demo() {
  const { currentTheme, theme } = useTheme();

  const { t } = useTranslation();
  const [form, setForm] = useState({
    customerId: DEMO_WIDGET_CONFIG.customerId,
    orderNumber: generateOrderNumber(),
    rewardPoints: '86.7',
    declaredValueTotal: '100',
  });
  const [items, setItems] = useState([
    { id: 'default-0006', sku: '0006', quantity: '1' },
    { id: 'default-0043', sku: '0043', quantity: '2' },
    { id: 'default-2864', sku: '2864', quantity: '1' },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  const normalizedItems = useMemo(() => normalizeItems(items), [items]);

  const canConfirm = useMemo(() => {
    const customerId = String(form.customerId || '').trim();
    const orderNumber = String(form.orderNumber || '').trim();
    const rewardPoints = parseNonNegativeFloatOrNull(form.rewardPoints);
    const declaredValueTotal = parseNonNegativeFloatOrNull(form.declaredValueTotal);
    return Boolean(
      customerId &&
        orderNumber &&
        rewardPoints != null &&
        declaredValueTotal != null &&
        normalizedItems.length > 0 &&
        !submitting,
    );
  }, [form, normalizedItems, submitting]);

  const widgetConfig = useMemo(() => {
    if (!payload?.id) return null;
    return {
      ...DEMO_WIDGET_CONFIG,
      ...getDemoServiceUrls(),
      customerId: String(payload.customer_id || form.customerId || DEMO_WIDGET_CONFIG.customerId),
      calculationPayloadId: String(payload.id),
    };
  }, [payload, form.customerId]);

  const srcdoc = useMemo(
    () => (widgetConfig ? buildWidgetSrcdoc(widgetConfig) : ''),
    [widgetConfig],
  );

  useEffect(() => {
    if (!DEMO_WIDGET_CONFIG.isDemo) return undefined;
    const expectedPayloadId = String(payload?.id || '').trim();
    if (!expectedPayloadId) return undefined;

    const handleMessage = (event) => {
      const data = event?.data;
      if (!data || data.type !== 'filuetship.widget.complete') return;

      const msgPayloadId = String(data.calculationPayloadId || '').trim();
      if (!msgPayloadId || msgPayloadId !== expectedPayloadId) return;

      // Widget in shipment-confirmation mode posts `confirmed: true` after success.
      if (data.confirmed === false) return;
      setForm((prev) => ({ ...prev, orderNumber: generateOrderNumber() }));
      setSuccessPopupOpen(true);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [payload?.id]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateItem = (id, key, value) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, [key]: value } : item)));
  };

  const addItem = () => {
    setItems((prev) => [...prev, createEmptyItem()]);
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const onConfirm = async (event) => {
    event.preventDefault();
    setError('');

    const customerId = String(form.customerId || '').trim();
    const orderNumber = String(form.orderNumber || '').trim();
    const rewardPoints = parseNonNegativeFloatOrNull(form.rewardPoints);
    const declaredValueTotal = parseNonNegativeFloatOrNull(form.declaredValueTotal);
    const payloadItems = normalizeItems(items);

    if (!customerId || !orderNumber || rewardPoints == null || declaredValueTotal == null || !payloadItems.length) {
      setError('Customer UID, order number, non-negative points, non-negative declared amount, and at least one item are required.');
      return;
    }

    setSubmitting(true);
    setPayload(null);
    try {
      const services = getDemoServiceUrls();
      const tokenData = await exchangeApiKeyToken(
        services.authIdentityUrl,
        DEMO_WIDGET_CONFIG.apiKey,
      );
      const created = await createCalculationPayload({
        deliveryCoreUrl: services.deliveryCoreUrl,
        token: tokenData.access_token,
        companyId: tokenData.company_id,
        body: {
          order_number: orderNumber,
          customer_id: customerId,
          language: 'en',
          environment: 'TEST',
          data: {
            reward_points: rewardPoints,
            declared_value_total: declaredValueTotal,
          },
          items: payloadItems,
        },
      });
      setPayload(created);
    } catch (err) {
      console.error('Demo confirm failed:', err);
      setError(err?.message || 'Failed to start demo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <SideMenu currentTheme={currentTheme} theme={theme} />
      <main className="demo-main">
        <h1 style={{ marginTop: 0, marginBottom: 16, color: currentTheme.text }}>{t('demo')}</h1>
        <div className="demo-shell">
    <div className="demo-page">
      <div className="demo-pane demo-pane--form">
        <header className="demo-header">
          <h1 className="demo-title">FiluetShip Demo</h1>
          <p className="demo-subtitle">
            Enter delivery options, confirm to create a calculation payload, then complete checkout in the
            widget.
          </p>
        </header>

        <form className="demo-form" onSubmit={onConfirm}>
          <label className="demo-field">
            <span className="demo-label">Customer UID</span>
            <input
              className="demo-input"
              value={form.customerId}
              onChange={(e) => updateField('customerId', e.target.value)}
              autoComplete="off"
              disabled={submitting}
            />
          </label>

          <label className="demo-field">
            <span className="demo-label">Order number</span>
            <div className="demo-input-with-action">
              <input
                className="demo-input demo-input--with-action"
                value={form.orderNumber}
                onChange={(e) => updateField('orderNumber', e.target.value)}
                autoComplete="off"
                disabled={submitting}
                placeholder="e.g. DEMO-1001"
              />
              <button
                type="button"
                className="demo-input-action"
                onClick={() => updateField('orderNumber', generateOrderNumber())}
                disabled={submitting}
                aria-label="Regenerate order number"
                title="Regenerate order number"
              >
                ↻
              </button>
            </div>
          </label>

          <label className="demo-field">
            <span className="demo-label">Points</span>
            <input
              className="demo-input"
              type="number"
              min="0"
              step="any"
              value={form.rewardPoints}
              onChange={(e) => updateField('rewardPoints', e.target.value)}
              disabled={submitting}
            />
          </label>

          <label className="demo-field">
            <span className="demo-label">Declared amount</span>
            <input
              className="demo-input"
              type="number"
              min="0"
              step="any"
              value={form.declaredValueTotal}
              onChange={(e) => updateField('declaredValueTotal', e.target.value)}
              disabled={submitting}
            />
          </label>

          <div className="demo-items">
            <div className="demo-items-header">
              <span className="demo-label">Order items</span>
              <button
                type="button"
                className="demo-secondary-btn"
                onClick={addItem}
                disabled={submitting}
              >
                Add item
              </button>
            </div>

            {items.length === 0 ? (
              <p className="demo-items-empty">Add at least one item to confirm.</p>
            ) : (
              <ul className="demo-items-list">
                {items.map((item) => (
                  <li key={item.id} className="demo-item-row">
                    <label className="demo-item-field">
                      <span className="demo-item-label">SKU</span>
                      <input
                        className="demo-input"
                        value={item.sku}
                        onChange={(e) => updateItem(item.id, 'sku', e.target.value)}
                        autoComplete="off"
                        disabled={submitting}
                        placeholder="0006"
                      />
                    </label>
                    <label className="demo-item-field demo-item-field--qty">
                      <span className="demo-item-label">Qty</span>
                      <input
                        className="demo-input"
                        type="number"
                        min="1"
                        step="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                        disabled={submitting}
                      />
                    </label>
                    <button
                      type="button"
                      className="demo-remove-btn"
                      onClick={() => removeItem(item.id)}
                      disabled={submitting}
                      aria-label="Remove item"
                      title="Remove item"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error ? <div className="demo-error">{error}</div> : null}

          <button className="demo-confirm" type="submit" disabled={!canConfirm}>
            {submitting ? 'Saving…' : 'Confirm'}
          </button>

          {payload?.id ? (
            <p className="demo-hint">
              Payload created: <code>{payload.id}</code>
            </p>
          ) : null}
        </form>
      </div>

      <div className="demo-pane demo-pane--widget">
        {srcdoc ? (
          <iframe
            className="demo-widget-frame"
            title="FiluetShip delivery widget"
            srcDoc={srcdoc}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        ) : (
          <div className="demo-widget-placeholder">
            <p>The delivery widget will appear here after you press Confirm.</p>
          </div>
        )}
      </div>

      {successPopupOpen && (
        <div
          className="demo-modalOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSuccessPopupOpen(false);
          }}
        >
          <div className="demo-modal">
            <div className="demo-modalTitle">Success</div>
            <div className="demo-modalBody">
              Shipment created successfully. You can close this window
            </div>
            <div className="demo-modalFooter">
              <button
                type="button"
                className="demo-primaryBtn"
                onClick={() => setSuccessPopupOpen(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
        </div>
      </main>
    </div>
  );
}
